import { sfQuery } from "../../API";

const coordinatorUserRole = "Client Experience Coordinator";

// Helper function to calculate business days between two dates
const getBusinessDaysBetween = (startDate, endDate) => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  let count = 0;
  const curDate = new Date(start.getTime());
  while (curDate <= end) {
    const dayOfWeek = curDate.getDay();
    if (dayOfWeek !== 0 && dayOfWeek !== 6) count++;
    curDate.setDate(curDate.getDate() + 1);
  }
  return count;
};

// Get list of coordinators for the filter dropdown
export const getCoordinatorsList = async (context) => {
  try {
    const coordinators = await sfQuery(
      "User",
      "Id, Name",
      `IsActive = true AND UserRole.Name = '${coordinatorUserRole}'`,
      context
    );

    console.log("✅ sfQuery result for coordinators:", coordinators);

    const mappedCoordinators = coordinators.map((user) => ({
      id: user.Id,
      name: user.Name,
    }));

    return mappedCoordinators;
  } catch (error) {
    console.error("❌ Error in getCoordinatorsList:", error);
    throw error;
  }
};

// Helper function to group data by coordinator
const groupByCoordinator = (
  data,
  coordinatorField = "Gargle_Coordinator__r.Name"
) => {
  const breakdown = {};
  data.forEach((item) => {
    const coordinatorName = getNestedValue(item, coordinatorField) || "Unknown";
    breakdown[coordinatorName] = (breakdown[coordinatorName] || 0) + 1;
  });
  return breakdown;
};

// Helper function to get nested object values
const getNestedValue = (obj, path) => {
  return path.split(".").reduce((current, key) => current?.[key], obj);
};

export const getActiveMemberships = async (
  context,
  selectedCoordinator = null
) => {
  let whereClause = `Membership_Status__c = 'Activated' AND (NOT Account.Name LIKE 'Test %')`;

  if (selectedCoordinator) {
    whereClause += ` AND Gargle_Coordinator__c = '${selectedCoordinator}'`;
  }

  const activeMemberships = await sfQuery(
    "Account",
    "Id, Name, OwnerId, Gargle_Coordinator__c, Gargle_Coordinator__r.Name, RecordType.Name",
    whereClause,
    context
  );

  // Count only Customer and Customer (CA) for the main number
  const customerAccounts = activeMemberships.filter((account) => {
    const recordType = account.RecordType?.Name || "";
    return recordType === "Customer" || recordType === "Customer (CA)";
  });

  const result = {
    count: customerAccounts.length,
    data: activeMemberships,
  };

  // Add breakdown by RecordType to show all types
  const recordTypeBreakdown = {};
  activeMemberships.forEach((account) => {
    const recordType = account.RecordType?.Name || "Unknown";
    recordTypeBreakdown[recordType] =
      (recordTypeBreakdown[recordType] || 0) + 1;
  });
  result.breakdown = recordTypeBreakdown;

  return result;
};

export const getCallPreps = async (context, selectedCoordinator = null) => {
  let whereClause = `Task_Type__c LIKE '%Call Prep%' AND Owner.UserRole.Name = '${coordinatorUserRole}' AND Status = 'Completed' AND ActivityDate = THIS_MONTH`;

  if (selectedCoordinator) {
    whereClause += ` AND OwnerId = '${selectedCoordinator}'`;
  }

  const result = await sfQuery(
    "Task",
    "Id, ActivityDate, CompletedDateTime, What.Name, Status, Owner.Name, OwnerId",
    whereClause,
    context
  );

  const today = new Date();
  const result2 = {
    Green: 0,
    Yellow: 0,
    Red: 0,
  };

  result.forEach((task) => {
    const completedDate = new Date(task.CompletedDateTime);
    const activityDate = new Date(task.ActivityDate);
    const diffTime = Math.abs(activityDate - completedDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays <= 1) {
      result2.Green++;
    } else if (diffDays <= 2) {
      result2.Yellow++;
    } else {
      result2.Red++;
    }
  });

  // Add breakdown by coordinator if no specific coordinator is selected
  if (!selectedCoordinator) {
    result2.breakdown = {};
    result.forEach((task) => {
      const coordinatorName = task.Owner?.Name || "Unknown";
      result2.breakdown[coordinatorName] =
        (result2.breakdown[coordinatorName] || 0) + 1;
    });
  }

  return result2;
};

// Get call preps this week
export const getCallPrepsThisWeek = async (
  context,
  selectedCoordinator = null
) => {
  let whereClause = `Task_Type__c LIKE '%Call Prep%' AND Owner.UserRole.Name = '${coordinatorUserRole}' AND Status = 'Completed' AND ActivityDate = THIS_WEEK`;

  if (selectedCoordinator) {
    whereClause += ` AND OwnerId = '${selectedCoordinator}'`;
  }

  const result = await sfQuery(
    "Task",
    "Id, ActivityDate, CompletedDateTime, What.Name, Status, Owner.Name, OwnerId",
    whereClause,
    context
  );

  const result2 = {
    count: result.length,
    Green: result.length,
    Yellow: 0,
    Red: 0,
  };

  // Add breakdown by coordinator if no specific coordinator is selected
  if (!selectedCoordinator) {
    result2.breakdown = {};
    result.forEach((task) => {
      const coordinatorName = task.Owner?.Name || "Unknown";
      result2.breakdown[coordinatorName] =
        (result2.breakdown[coordinatorName] || 0) + 1;
    });
  }

  return result2;
};

// Get call preps this month
export const getCallPrepsThisMonth = async (
  context,
  selectedCoordinator = null
) => {
  let whereClause = `Task_Type__c LIKE '%Call Prep%' AND Owner.UserRole.Name = '${coordinatorUserRole}' AND Status = 'Completed' AND ActivityDate = THIS_MONTH`;

  if (selectedCoordinator) {
    whereClause += ` AND OwnerId = '${selectedCoordinator}'`;
  }

  const result = await sfQuery(
    "Task",
    "Id, ActivityDate, CompletedDateTime, What.Name, Status, Owner.Name, OwnerId",
    whereClause,
    context
  );

  const result2 = {
    count: result.length,
    Green: result.length,
    Yellow: 0,
    Red: 0,
  };

  // Add breakdown by coordinator if no specific coordinator is selected
  if (!selectedCoordinator) {
    result2.breakdown = {};
    result.forEach((task) => {
      const coordinatorName = task.Owner?.Name || "Unknown";
      result2.breakdown[coordinatorName] =
        (result2.breakdown[coordinatorName] || 0) + 1;
    });
  }

  return result2;
};

// Get upcoming GM order expirations
export const getUpcomingGMOrderExpirations = async (
  context,
  selectedCoordinator = null
) => {
  const today = new Date();
  const futureDate = new Date();
  futureDate.setDate(today.getDate() + 45); // 45 days from now

  const todayString = today.toISOString().split("T")[0];
  const futureDateString = futureDate.toISOString().split("T")[0];

  let whereClause = `Account.Membership_Status__c = 'Activated' AND Order_Label__c LIKE 'GM-%' AND Contract_Expiration_Date__c >= ${todayString} AND Contract_Expiration_Date__c <= ${futureDateString}`;

  if (selectedCoordinator) {
    whereClause += ` AND Account.Gargle_Coordinator__c = '${selectedCoordinator}'`;
  }

  const result = await sfQuery(
    "Order",
    "Id, Contract_Expiration_Date__c, Account.Name, Account.Gargle_Coordinator__r.Name, Account.Gargle_Coordinator__c, Order_Label__c",
    whereClause,
    context
  );

  const result2 = {
    Green: 0,
    Yellow: 0,
    Red: 0,
  };

  result.forEach((order) => {
    const expirationDate = new Date(order.Contract_Expiration_Date__c);
    const diffTime = expirationDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 10) {
      result2.Red++;
    } else if (diffDays < 20) {
      result2.Yellow++;
    } else {
      result2.Green++;
    }
  });

  // Add breakdown by coordinator if no specific coordinator is selected
  if (!selectedCoordinator) {
    result2.breakdown = groupByCoordinator(
      result,
      "Account.Gargle_Coordinator__r.Name"
    );
  }

  return result2;
};

export const getCallPrepsLast7Days = async (
  context,
  selectedCoordinator = null
) => {
  let whereClause = "CreatedDate = LAST_N_DAYS:7";

  if (selectedCoordinator) {
    whereClause += ` AND Account__r.Gargle_Coordinator__c = '${selectedCoordinator}'`;
  }

  const callPrepsLast7Days = await sfQuery(
    "Call_Prep__c",
    "Id, CreatedDate, Account__r.Name, Account__r.Gargle_Coordinator__r.Name, Account__r.Gargle_Coordinator__c",
    whereClause,
    context
  );

  const result = {
    count: callPrepsLast7Days.length,
    data: callPrepsLast7Days,
  };

  // Add breakdown by coordinator if no specific coordinator is selected
  if (!selectedCoordinator) {
    result.breakdown = groupByCoordinator(
      callPrepsLast7Days,
      "Account__r.Gargle_Coordinator__r.Name"
    );
  }

  return result;
};

export const getAuditsThisMonth = async (
  context,
  selectedCoordinator = null
) => {
  let whereClause = `Task_Type__c LIKE 'Account Audit' AND Owner.UserRole.Name = '${coordinatorUserRole}' AND Status = 'Completed' AND ActivityDate = THIS_MONTH`;

  if (selectedCoordinator) {
    whereClause += ` AND OwnerId = '${selectedCoordinator}'`;
  }

  const result = await sfQuery(
    "Task",
    "Id, ActivityDate, CompletedDateTime, What.Name, Status, Owner.Name, OwnerId",
    whereClause,
    context
  );

  const result2 = {
    count: result.length,
    Green: result.length,
    Yellow: 0,
    Red: 0,
  };

  // Add breakdown by coordinator if no specific coordinator is selected
  if (!selectedCoordinator) {
    result2.breakdown = {};
    result.forEach((task) => {
      const coordinatorName = task.Owner?.Name || "Unknown";
      result2.breakdown[coordinatorName] =
        (result2.breakdown[coordinatorName] || 0) + 1;
    });
  }

  return result2;
};

export const getOverdueTasks = async (context, selectedCoordinator = null) => {
  let whereClause = `Status = 'Open' AND Owner.UserRole.Name = '${coordinatorUserRole}' AND ActivityDate < TODAY AND WhatId IN (SELECT Id FROM Account WHERE Membership_Status__c = 'Activated')`;

  if (selectedCoordinator) {
    whereClause += ` AND OwnerId = '${selectedCoordinator}'`;
  }

  const overdueTasks = await sfQuery(
    "Task",
    "Id, WhatId, What.Name, ActivityDate, Status, Owner.Name, Subject, OwnerId",
    whereClause,
    context
  );

  const result = {
    count: overdueTasks.length,
    data: overdueTasks,
  };

  // Add breakdown by coordinator if no specific coordinator is selected
  if (!selectedCoordinator) {
    result.breakdown = {};
    overdueTasks.forEach((task) => {
      const coordinatorName = task.Owner?.Name || "Unknown";
      result.breakdown[coordinatorName] =
        (result.breakdown[coordinatorName] || 0) + 1;
    });
  }

  return result;
};

export const getAccountsWithNoOpenActivities = async (
  context,
  selectedCoordinator = null
) => {
  let accountWhereClause = `Membership_Status__c = 'Activated'`;
  if (selectedCoordinator) {
    accountWhereClause += ` AND Gargle_Coordinator__c = '${selectedCoordinator}'`;
  }

  const accountsWithNoOpenActivities = await sfQuery(
    "Account",
    "Id, Name, Gargle_Coordinator__r.Name, Gargle_Coordinator__c, (SELECT Id FROM Tasks WHERE Status = 'Open')",
    accountWhereClause,
    context
  );

  const accounts = accountsWithNoOpenActivities.filter(
    (account) => !account.Tasks || account.Tasks.records.length === 0
  );

  const result = {
    count: accounts.length,
    data: accounts,
  };

  // Add breakdown by coordinator if no specific coordinator is selected
  if (!selectedCoordinator) {
    result.breakdown = groupByCoordinator(
      accounts,
      "Gargle_Coordinator__r.Name"
    );
  }

  return result;
};

export const getOverdueInvoices = async (
  context,
  selectedCoordinator = null
) => {
  let whereClause = `Account.Membership_Status__c = 'Activated' AND Order_Label__c LIKE 'MI-%' AND Status = 'Activated' AND Payment_Date_new__c < TODAY AND Amount_Owed__c > 0`;

  if (selectedCoordinator) {
    whereClause += ` AND Account.Gargle_Coordinator__c = '${selectedCoordinator}'`;
  }

  const result = await sfQuery(
    "Order",
    "Id, Account.Name, Payment_Date_new__c, Total_Amount__c, Account.Gargle_Coordinator__r.Name, Account.Gargle_Coordinator__c",
    whereClause,
    context
  );

  const today = new Date();
  const result2 = {
    Green: 0,
    Yellow: 0,
    Red: 0,
  };

  result.forEach((order) => {
    const dueDate = new Date(order.Payment_Date_new__c);
    const overdueDays = Math.floor((today - dueDate) / (1000 * 60 * 60 * 24));
    if (overdueDays <= 5) result2.Yellow++;
    else result2.Red++;
  });

  // Add breakdown by coordinator if no specific coordinator is selected
  if (!selectedCoordinator) {
    result2.breakdown = groupByCoordinator(
      result,
      "Account.Gargle_Coordinator__r.Name"
    );
  }

  return result2;
};

export const getAccountsByProduct = async (
  context,
  selectedCoordinator = null
) => {
  let whereClause = `Order.Account.Membership_Status__c = 'Activated' AND Order.Order_Label__c LIKE 'GM-%' AND Order.Status IN ('Activated', 'Ala Carte Non Member') AND Is_Active__c = TRUE AND Product_Available__c = TRUE AND Is_Cancelled__c = FALSE AND (NOT Product2.ProductCode IN ('PC', 'DMPC', 'NM', 'DMNM', 'RC', 'DMRC'))`;

  if (selectedCoordinator) {
    whereClause += ` AND Order.Account.Gargle_Coordinator__c = '${selectedCoordinator}'`;
  }

  const accountsByProduct = await sfQuery(
    "OrderItem",
    "PricebookEntry.Product2.Name, Order.Account.Name, Order.AccountId, Order.Account.Gargle_Coordinator__r.Name, Order.Account.Gargle_Coordinator__c",
    whereClause,
    context
  );

  const result = {};
  accountsByProduct.forEach((item) => {
    const product = item.PricebookEntry.Product2.Name || "Unknown";

    // Group similar products
    let groupedProduct = product;
    if (product.startsWith("Website")) {
      groupedProduct = "Website/SEO";
    } else if (product.startsWith("Social Media")) {
      groupedProduct = "Social Media";
    } else if (product.startsWith("Social+")) {
      groupedProduct = "Social+";
    } else if (product.startsWith("Gargle Social+")) {
      groupedProduct = "Social+";
    } else if (product.startsWith("Google Ads")) {
      groupedProduct = "Google Ads";
    } else if (product.startsWith("Swell")) {
      groupedProduct = "Swell";
    } else if (product.startsWith("Call Tracking")) {
      groupedProduct = "Call Tracking";
    } else if (product.startsWith("Gargle Data")) {
      groupedProduct = "Gargle Data";
    } else if (product.startsWith("Full Suite")) {
      groupedProduct = "Full Suite";
    } else if (product.startsWith("Ad Agency")) {
      groupedProduct = "Ad Agency";
    }

    if (!result[groupedProduct]) {
      result[groupedProduct] = 0;
    }
    result[groupedProduct]++;
  });

  // Add breakdown by coordinator if no specific coordinator is selected
  if (!selectedCoordinator) {
    const coordinatorBreakdown = {};
    accountsByProduct.forEach((item) => {
      const coordinatorName =
        item.Order?.Account?.Gargle_Coordinator__r?.Name || "Unknown";
      coordinatorBreakdown[coordinatorName] =
        (coordinatorBreakdown[coordinatorName] || 0) + 1;
    });
    result.breakdown = coordinatorBreakdown;
  }

  return result;
};

export const getCompletedDirectorAccountReviews = async (
  context,
  selectedCoordinator = null
) => {
  let whereClause = `Subject LIKE '%Director Account Review%' AND Owner.UserRole.Name = '${coordinatorUserRole}' AND Status = 'Completed' AND ActivityDate = THIS_MONTH`;

  if (selectedCoordinator) {
    whereClause += ` AND OwnerId = '${selectedCoordinator}'`;
  }

  const result = await sfQuery(
    "Task",
    "Id, ActivityDate, CompletedDateTime, What.Name, Status, Owner.Name, OwnerId",
    whereClause,
    context
  );

  const today = new Date();
  const result2 = {
    Green: 0,
    Yellow: 0,
    Red: 0,
  };

  result.forEach((task) => {
    const completedDate = new Date(task.CompletedDateTime);
    const activityDate = new Date(task.ActivityDate);
    const diffTime = Math.abs(activityDate - completedDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays <= 1) {
      result2.Green++;
    } else if (diffDays <= 2) {
      result2.Yellow++;
    } else {
      result2.Red++;
    }
  });

  // Add breakdown by coordinator if no specific coordinator is selected
  if (!selectedCoordinator) {
    result2.breakdown = {};
    result.forEach((task) => {
      const coordinatorName = task.Owner?.Name || "Unknown";
      result2.breakdown[coordinatorName] =
        (result2.breakdown[coordinatorName] || 0) + 1;
    });
  }

  return result2;
};

export const getPausedProducts = async (
  context,
  selectedCoordinator = null
) => {
  let whereClause = `Paused__c = true AND Order.Account.Membership_Status__c = 'Activated' AND Order.Order_Label__c LIKE 'GM-%'`;

  if (selectedCoordinator) {
    whereClause += ` AND Order.Account.Gargle_Coordinator__c = '${selectedCoordinator}'`;
  }

  const result = await sfQuery(
    "OrderItem",
    "Id, Paused__c, Paused_Date__c, Order.Account.Name, Order.Account.Gargle_Coordinator__r.Name, Order.Account.Gargle_Coordinator__c",
    whereClause,
    context
  );

  const today = new Date();
  const result2 = {
    Green: 0,
    Yellow: 0,
    Red: 0,
  };

  result.forEach((item) => {
    if (!item.Paused_Date__c) {
      result2.Red++;
      return;
    }

    const pausedUntil = new Date(item.Paused_Date__c);
    const diffTime = Math.abs(pausedUntil - today);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays <= 7) {
      result2.Green++;
    } else if (diffDays <= 14) {
      result2.Yellow++;
    } else {
      result2.Red++;
    }
  });

  // Add breakdown by coordinator if no specific coordinator is selected
  if (!selectedCoordinator) {
    result2.breakdown = groupByCoordinator(
      result,
      "Order.Account.Gargle_Coordinator__r.Name"
    );
  }

  return result2;
};

export const getCompletionOfSAItasks = async (
  context,
  selectedCoordinator = null
) => {
  let whereClause = `Subject LIKE '%SAI%' AND Owner.UserRole.Name = '${coordinatorUserRole}' AND Status = 'Completed' AND ActivityDate = THIS_MONTH`;

  if (selectedCoordinator) {
    whereClause += ` AND OwnerId = '${selectedCoordinator}'`;
  }

  const result = await sfQuery(
    "Task",
    "Id, Subject, ActivityDate, CompletedDateTime, Status, Owner.Name, OwnerId",
    whereClause,
    context
  );

  const result2 = {
    Green: 0,
    Yellow: 0,
    Red: 0,
  };

  result.forEach((task) => {
    const completedDate = new Date(task.CompletedDateTime);
    const activityDate = new Date(task.ActivityDate);
    const diffTime = Math.abs(activityDate - completedDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays <= 1) {
      result2.Green++;
    } else if (diffDays <= 2) {
      result2.Yellow++;
    } else {
      result2.Red++;
    }
  });

  // Add breakdown by coordinator if no specific coordinator is selected
  if (!selectedCoordinator) {
    result2.breakdown = {};
    result.forEach((task) => {
      const coordinatorName = task.Owner?.Name || "Unknown";
      result2.breakdown[coordinatorName] =
        (result2.breakdown[coordinatorName] || 0) + 1;
    });
  }

  return result2;
};

// Get open tasks for bottom table
export const getOpenTasks = async (context, selectedCoordinator = null) => {
  let whereClause = `Status = 'Open' AND Owner.UserRole.Name = '${coordinatorUserRole}' AND ActivityDate <= NEXT_WEEK AND (NOT Subject LIKE 'Unresolved Email:%')`;

  if (selectedCoordinator) {
    whereClause += ` AND OwnerId = '${selectedCoordinator}'`;
  }

  const result = await sfQuery(
    "Task",
    "Id, Subject, Task_Type__c, ActivityDate, Description, Owner.Name, OwnerId, What.Name",
    whereClause,
    context
  );

  return result.map((task) => ({
    ...task,
    AccountName: task.What?.Name || "Unknown",
    TaskType: task.Task_Type__c || "Unknown",
    Date: task.ActivityDate,
    Details: task.Description || "No details",
    Owner: task.Owner?.Name || "Unknown",
  }));
};

export const getDMOnlyReport = async (context) => {
  const orders = await sfQuery(
    "Order",
    "Id, AccountId, Total_Active_Products_DM__c",
    `Order_Label__c LIKE 'GM-%' AND Status = 'Activated' AND Total_Active_Products_membership__c = 1 AND (NOT Account.Name LIKE 'Test %')`,
    context
  );

  const dmOnlyAccounts = orders.filter(
    (order) => order.Total_Active_Products_DM__c > 0
  );

  return {
    count: dmOnlyAccounts.length,
    data: dmOnlyAccounts,
  };
};

export const getGMOnlyReport = async (context) => {
  const orders = await sfQuery(
    "Order",
    "Id, AccountId, Total_Active_Products_DM__c",
    `Order_Label__c LIKE 'GM-%' AND Status = 'Activated' AND Total_Active_Products_membership__c = 1 AND (NOT Account.Name LIKE 'Test %')`,
    context
  );

  const gmOnlyAccounts = orders.filter(
    (order) =>
      !order.Total_Active_Products_DM__c ||
      order.Total_Active_Products_DM__c === 0
  );

  return {
    count: gmOnlyAccounts.length,
    data: gmOnlyAccounts,
  };
};

export const getTasksCompletedThisWeek = async (
  context,
  selectedCoordinator = null
) => {
  let whereClause = `Status = 'Completed' AND Owner.UserRole.Name = '${coordinatorUserRole}' AND ActivityDate = THIS_WEEK`;

  if (selectedCoordinator) {
    whereClause += ` AND OwnerId = '${selectedCoordinator}'`;
  }

  const tasksCompletedThisWeek = await sfQuery(
    "Task",
    "Id, Subject, WhatId, What.Name, ActivityDate, Status, Owner.Name, OwnerId",
    whereClause,
    context
  );

  const result = {
    count: tasksCompletedThisWeek.length,
    data: tasksCompletedThisWeek,
  };

  // Add breakdown by coordinator if no specific coordinator is selected
  if (!selectedCoordinator) {
    result.breakdown = {};
    tasksCompletedThisWeek.forEach((task) => {
      const coordinatorName = task.Owner?.Name || "Unknown";
      result.breakdown[coordinatorName] =
        (result.breakdown[coordinatorName] || 0) + 1;
    });
  }

  return result;
};

export const getTasksCompletedThisMonth = async (
  context,
  selectedCoordinator = null
) => {
  let whereClause = `Status = 'Completed' AND Owner.UserRole.Name = '${coordinatorUserRole}' AND ActivityDate = THIS_MONTH`;

  if (selectedCoordinator) {
    whereClause += ` AND OwnerId = '${selectedCoordinator}'`;
  }

  const tasksCompletedThisMonth = await sfQuery(
    "Task",
    "Id, Subject, WhatId, What.Name, ActivityDate, Status, Owner.Name, OwnerId",
    whereClause,
    context
  );

  const result = {
    count: tasksCompletedThisMonth.length,
    data: tasksCompletedThisMonth,
  };

  // Add breakdown by coordinator if no specific coordinator is selected
  if (!selectedCoordinator) {
    result.breakdown = {};
    tasksCompletedThisMonth.forEach((task) => {
      const coordinatorName = task.Owner?.Name || "Unknown";
      result.breakdown[coordinatorName] =
        (result.breakdown[coordinatorName] || 0) + 1;
    });
  }

  return result;
};

// ====================================
// DATATABLE-SPECIFIC QUERY FUNCTIONS
// ====================================
// These functions return actual record arrays for DataTable display

export const getCallPrepsForTable = async (context) => {
  const result = await sfQuery(
    "Task",
    "Id, ActivityDate, CompletedDateTime, What.Name, Status, Subject, Owner.Name, WhatId",
    `Task_Type__c LIKE '%Call Prep%' AND Owner.UserRole.Name = '${coordinatorUserRole}' AND Status = 'Completed' AND ActivityDate = THIS_MONTH`,
    context
  );

  return result.map((task) => {
    const completedDate = new Date(task.CompletedDateTime);
    const activityDate = new Date(task.ActivityDate);
    const diffTime = Math.abs(activityDate - completedDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return {
      ...task,
      Name: task.What?.Name || "Unknown Account",
      Owner: { Name: task.Owner?.Name || "Unknown" },
      DueDate: task.ActivityDate,
      DaysBetween: diffDays,
      AccountId: task.WhatId,
    };
  });
};

export const getCompletedDirectorAccountReviewsForTable = async (context) => {
  const result = await sfQuery(
    "Task",
    "Id, ActivityDate, CompletedDateTime, What.Name, Status, Subject, Owner.Name, WhatId",
    `Subject LIKE '%Director Account Review%' AND Owner.UserRole.Name = '${coordinatorUserRole}' AND Status = 'Completed' AND ActivityDate = THIS_MONTH`,
    context
  );

  return result.map((task) => {
    const completedDate = new Date(task.CompletedDateTime);
    const activityDate = new Date(task.ActivityDate);
    const diffTime = Math.abs(activityDate - completedDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return {
      ...task,
      Name: task.What?.Name || "Unknown Account",
      Owner: { Name: task.Owner?.Name || "Unknown" },
      ReviewDate: task.ActivityDate,
      DaysBetween: diffDays,
      AccountId: task.WhatId,
    };
  });
};

export const getOverdueInvoicesForTable = async (context) => {
  const result = await sfQuery(
    "Order",
    "Id, Account.Name, Account.Owner.Name, Payment_Date_new__c, Total_Amount__c, AccountId",
    `Account.Membership_Status__c = 'Activated' AND Order_Label__c LIKE 'MI-%' AND Status = 'Activated' AND Payment_Date_new__c < TODAY AND Amount_Owed__c > 0`,
    context
  );

  return result.map((order) => ({
    ...order,
    Id: order.AccountId, // Use AccountId as the record ID for navigation
    Name: order.Account?.Name || "Unknown Account",
    Owner: { Name: order.Account?.Owner?.Name || "Unknown" },
    DueDate: order.Payment_Date_new__c,
    Amount: order.Total_Amount__c,
    InvoiceNumber: order.Id, // Use Order ID as invoice number
  }));
};

export const getPausedProductsForTable = async (context) => {
  const result = await sfQuery(
    "OrderItem",
    "Id, Paused__c, Paused_Date__c, Order.Account.Name, Order.Account.Owner.Name, Order.AccountId, PricebookEntry.Product2.Name",
    `Paused__c = true AND Order.Account.Membership_Status__c = 'Activated' AND Order.Order_Label__c LIKE 'GM-%'`,
    context
  );

  return result.map((item) => ({
    ...item,
    Id: item.Order?.AccountId, // Use AccountId for navigation
    Name: item.Order?.Account?.Name || "Unknown Account",
    Owner: { Name: item.Order?.Account?.Owner?.Name || "Unknown" },
    Product: item.PricebookEntry?.Product2?.Name || "Unknown Product",
    ExpirationDate: item.Paused_Date__c,
    AccountId: item.Order?.AccountId,
  }));
};

export const getCompletionOfSAItasksForTable = async (context) => {
  const result = await sfQuery(
    "Task",
    "Id, Subject, ActivityDate, CompletedDateTime, Status, Owner.Name, What.Name, WhatId",
    `Subject LIKE '%SAI%' AND Owner.UserRole.Name = '${coordinatorUserRole}' AND Status = 'Completed' AND ActivityDate = THIS_MONTH`,
    context
  );

  return result.map((task) => {
    const completedDate = new Date(task.CompletedDateTime);
    const activityDate = new Date(task.ActivityDate);
    const diffTime = Math.abs(activityDate - completedDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return {
      ...task,
      Name: task.What?.Name || "Unknown Account",
      Owner: { Name: task.Owner?.Name || "Unknown" },
      CompletionDate: task.CompletedDateTime,
      DaysBetween: diffDays,
      AccountId: task.WhatId,
    };
  });
};

export const getAccountsByProductForTable = async (context) => {
  const accountsByProduct = await sfQuery(
    "OrderItem",
    "PricebookEntry.Product2.Name, Order.Account.Name, Order.Account.Owner.Name, Order.AccountId, Order.Status",
    `Order.Account.Membership_Status__c = 'Activated' AND Order.Order_Label__c LIKE 'GM-%' AND Order.Status IN ('Activated', 'Ala Carte Non Member') AND Is_Active__c = TRUE AND Product_Available__c = TRUE AND Is_Cancelled__c = FALSE AND (NOT Product2.ProductCode IN ('PC', 'DMPC', 'NM', 'DMNM', 'RC', 'DMRC'))`,
    context
  );

  return accountsByProduct.map((item) => ({
    Id: item.Order?.AccountId,
    Name: item.Order?.Account?.Name || "Unknown Account",
    Owner: { Name: item.Order?.Account?.Owner?.Name || "Unknown" },
    Product: item.PricebookEntry?.Product2?.Name || "Unknown Product",
    Status: item.Order?.Status || "Unknown",
    AccountId: item.Order?.AccountId,
  }));
};

export const getActiveMembershipsForTable = async (context) => {
  const activeMemberships = await sfQuery(
    "Account",
    "Id, Name, Owner.Name, Membership_Status__c, Activated_Date__c",
    `Membership_Status__c = 'Activated' AND (NOT Account.Name LIKE 'Test %') AND RecordType.Name = 'Customer'`,
    context
  );

  return activeMemberships.map((account) => ({
    ...account,
    Owner: { Name: account.Owner?.Name || "Unknown" },
    Status: account.Membership_Status__c,
    ActivatedDate: account.Activated_Date__c,
  }));
};

export const getAuditsThisMonthForTable = async (context) => {
  const result = await sfQuery(
    "Task",
    "Id, ActivityDate, CompletedDateTime, What.Name, Status, Subject, Owner.Name, WhatId",
    `Task_Type__c LIKE 'Account Audit' AND Owner.UserRole.Name = '${coordinatorUserRole}' AND Status = 'Completed' AND ActivityDate = THIS_MONTH`,
    context
  );

  return result.map((task) => ({
    ...task,
    Name: task.What?.Name || "Unknown Account",
    Owner: { Name: task.Owner?.Name || "Unknown" },
    AuditDate: task.ActivityDate,
    AccountId: task.WhatId,
  }));
};

export const getCallPrepsThisWeekForTable = async (context) => {
  const result = await sfQuery(
    "Task",
    "Id, ActivityDate, CompletedDateTime, What.Name, Status, Subject, Owner.Name, WhatId",
    `Task_Type__c LIKE '%Call Prep%' AND Owner.UserRole.Name = '${coordinatorUserRole}' AND Status = 'Completed' AND ActivityDate = THIS_WEEK`,
    context
  );

  return result.map((task) => ({
    ...task,
    Name: task.What?.Name || "Unknown Account",
    Owner: { Name: task.Owner?.Name || "Unknown" },
    TaskDate: task.ActivityDate,
    AccountId: task.WhatId,
  }));
};

export const getCallPrepsThisMonthForTable = async (context) => {
  const result = await sfQuery(
    "Task",
    "Id, ActivityDate, CompletedDateTime, What.Name, Status, Subject, Owner.Name, WhatId",
    `Task_Type__c LIKE '%Call Prep%' AND Owner.UserRole.Name = '${coordinatorUserRole}' AND Status = 'Completed' AND ActivityDate = THIS_MONTH`,
    context
  );

  return result.map((task) => ({
    ...task,
    Name: task.What?.Name || "Unknown Account",
    Owner: { Name: task.Owner?.Name || "Unknown" },
    TaskDate: task.ActivityDate,
    AccountId: task.WhatId,
  }));
};

export const getUpcomingGMOrderExpirationsForTable = async (context) => {
  const today = new Date();
  const futureDate = new Date();
  futureDate.setDate(today.getDate() + 45); // 45 days from now

  const todayString = today.toISOString().split("T")[0];
  const futureDateString = futureDate.toISOString().split("T")[0];

  const result = await sfQuery(
    "Order",
    "Id, Contract_Expiration_Date__c, Account.Name, Account.Gargle_Coordinator__r.Name, Account.Id, Order_Label__c",
    `Account.Membership_Status__c = 'Activated' AND Order_Label__c LIKE 'GM-%' AND Contract_Expiration_Date__c >= ${todayString} AND Contract_Expiration_Date__c <= ${futureDateString}`,
    context
  );

  return result.map((order) => {
    const expirationDate = new Date(order.Contract_Expiration_Date__c);
    const diffTime = expirationDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return {
      ...order,
      Id: order.Account?.Id,
      Name: order.Account?.Name || "Unknown Account",
      Owner: { Name: order.Account?.Gargle_Coordinator__r?.Name || "Unknown" },
      ExpirationDate: order.Contract_Expiration_Date__c,
      DaysUntilExpiration: diffDays,
      OrderLabel: order.Order_Label__c,
      AccountId: order.Account?.Id,
    };
  });
};

// Add new query functions for Coordinators dashboard cards

// Accounts with inactive AE - accounts with membership_status__c = activated, Commissional_VP__r.Owner.IsActive=false
export const getAccountsWithInactiveAE = async (
  context,
  selectedCoordinator = null
) => {
  let whereClause = `Membership_Status__c = 'Activated' AND Commissional_VP__r.IsActive = false`;

  if (selectedCoordinator) {
    whereClause += ` AND Gargle_Coordinator__c = '${selectedCoordinator}'`;
  }

  const result = await sfQuery(
    "Account",
    "Id, Name, Gargle_Coordinator__c, Gargle_Coordinator__r.Name, Membership_Status__c, Commissional_VP__r.Name, Commissional_VP__r.IsActive",
    whereClause,
    context
  );

  const response = {
    count: result.length,
    data: result,
  };

  // Add breakdown by coordinator if no specific coordinator is selected
  if (!selectedCoordinator) {
    response.breakdown = groupByCoordinator(
      result,
      "Gargle_Coordinator__r.Name"
    );
  }

  return response;
};

// Concerned - accounts with Membership_Status__c = activated, Risk_of_Cancellation__c = Concerned
export const getConcernedAccounts = async (
  context,
  selectedCoordinator = null
) => {
  let whereClause = `Membership_Status__c = 'Activated' AND Risk_of_Cancellation__c = 'Concerned'`;

  if (selectedCoordinator) {
    whereClause += ` AND Gargle_Coordinator__c = '${selectedCoordinator}'`;
  }

  const result = await sfQuery(
    "Account",
    "Id, Name, Gargle_Coordinator__c, Gargle_Coordinator__r.Name, Membership_Status__c, Risk_of_Cancellation__c",
    whereClause,
    context
  );

  const response = {
    count: result.length,
    data: result,
    color: result.length > 5 ? "red" : result.length > 1 ? "yellow" : "green",
  };

  // Add breakdown by coordinator if no specific coordinator is selected
  if (!selectedCoordinator) {
    response.breakdown = groupByCoordinator(
      result,
      "Gargle_Coordinator__r.Name"
    );
  }

  return response;
};

// High Risk - accounts with Membership_Status__c = activated, Risk_of_Cancellation__c = High Risk
export const getHighRiskAccounts = async (
  context,
  selectedCoordinator = null
) => {
  let whereClause = `Membership_Status__c = 'Activated' AND Risk_of_Cancellation__c = 'High Risk'`;

  if (selectedCoordinator) {
    whereClause += ` AND Gargle_Coordinator__c = '${selectedCoordinator}'`;
  }

  const result = await sfQuery(
    "Account",
    "Id, Name, Gargle_Coordinator__c, Gargle_Coordinator__r.Name, Membership_Status__c, Risk_of_Cancellation__c",
    whereClause,
    context
  );

  const response = {
    count: result.length,
    data: result,
    color: result.length > 1 ? "red" : "green",
  };

  // Add breakdown by coordinator if no specific coordinator is selected
  if (!selectedCoordinator) {
    response.breakdown = groupByCoordinator(
      result,
      "Gargle_Coordinator__r.Name"
    );
  }

  return response;
};

// Cancelling - accounts with Membership_Status__c = activated, Risk_of_Cancellation__c = Cancelling
export const getCancellingAccounts = async (
  context,
  selectedCoordinator = null
) => {
  let whereClause = `Membership_Status__c = 'Activated' AND Risk_of_Cancellation__c = 'Cancelling'`;

  if (selectedCoordinator) {
    whereClause += ` AND Gargle_Coordinator__c = '${selectedCoordinator}'`;
  }

  const result = await sfQuery(
    "Account",
    "Id, Name, Gargle_Coordinator__c, Gargle_Coordinator__r.Name, Membership_Status__c, Risk_of_Cancellation__c",
    whereClause,
    context
  );

  const response = {
    count: result.length,
    data: result,
    color: result.length > 1 ? "red" : "green",
  };

  // Add breakdown by coordinator if no specific coordinator is selected
  if (!selectedCoordinator) {
    response.breakdown = groupByCoordinator(
      result,
      "Gargle_Coordinator__r.Name"
    );
  }

  return response;
};

// Get today's tasks for bottom table (instead of open tasks)
export const getTodaysTasks = async (context, selectedCoordinator = null) => {
  let whereClause = `Status = 'Open' AND Owner.UserRole.Name = '${coordinatorUserRole}' AND ActivityDate = TODAY AND (NOT Subject LIKE 'Unresolved Email:%')`;

  if (selectedCoordinator) {
    whereClause += ` AND OwnerId = '${selectedCoordinator}'`;
  }

  const result = await sfQuery(
    "Task",
    "Id, Subject, Task_Type__c, ActivityDate, Description, Owner.Name, OwnerId, What.Name",
    whereClause,
    context
  );

  return result.map((task) => ({
    ...task,
    AccountName: task.What?.Name || "Unknown",
    TaskType: task.Task_Type__c || "Unknown",
    Date: task.ActivityDate,
    Details: task.Description || "No details",
    Owner: task.Owner?.Name || "Unknown",
  }));
};

// Get director's today's tasks for a specific coordinator
export const getDirectorsTodaysTasks = async (
  context,
  selectedCoordinator = null
) => {
  if (!selectedCoordinator) {
    return [];
  }

  // Get accounts assigned to this coordinator to find their directors
  const coordinatorAccounts = await sfQuery(
    "Account",
    "Id, Name, OwnerId, Owner.Name, Gargle_Coordinator__c",
    `Gargle_Coordinator__c = '${selectedCoordinator}' AND Membership_Status__c = 'Activated'`,
    context
  );

  if (!coordinatorAccounts || coordinatorAccounts.length === 0) {
    return [];
  }

  // Get unique director IDs from the accounts
  const directorIds = [
    ...new Set(coordinatorAccounts.map((account) => account.OwnerId)),
  ];

  if (directorIds.length === 0) {
    return [];
  }

  // Get director's today's tasks
  const whereClause = `Status = 'Open' AND OwnerId IN (${directorIds
    .map((id) => `'${id}'`)
    .join(
      ","
    )}) AND ActivityDate = TODAY AND (NOT Subject LIKE 'Unresolved Email:%')`;

  const result = await sfQuery(
    "Task",
    "Id, Subject, Task_Type__c, ActivityDate, Description, Owner.Name, OwnerId, What.Name",
    whereClause,
    context
  );

  return result.map((task) => ({
    ...task,
    AccountName: task.What?.Name || "Unknown",
    TaskType: task.Task_Type__c || "Unknown",
    Date: task.ActivityDate,
    Details: task.Description || "No details",
    Owner: task.Owner?.Name || "Unknown",
    isDirector: true,
    directorName: task.Owner?.Name || "Unknown Director",
  }));
};

// Get overdue DARs (Director Account Reviews that are overdue by 120+ days)
export const getOverdueDARs = async (context, selectedCoordinator = null) => {
  // First get all active accounts
  let accountsWhereClause = `Membership_Status__c = 'Activated' AND (NOT Account.Name LIKE 'Test %')`;

  if (selectedCoordinator) {
    accountsWhereClause += ` AND Gargle_Coordinator__c = '${selectedCoordinator}'`;
  }

  const activeAccounts = await sfQuery(
    "Account",
    "Id, Name, Gargle_Coordinator__c, Gargle_Coordinator__r.Name",
    accountsWhereClause,
    context
  );

  // Get the last completed DAR for each account
  const accountIds = activeAccounts.map((account) => account.Id);

  if (accountIds.length === 0) {
    return {
      count: 0,
      Green: 0,
      Yellow: 0,
      Red: 0,
      data: [],
      breakdown: {},
    };
  }

  const lastDARs = await sfQuery(
    "Task",
    "Id, WhatId, What.Name, ActivityDate, CompletedDateTime, Owner.Name, OwnerId, Subject",
    `WhatId IN (${accountIds
      .map((id) => `'${id}'`)
      .join(
        ","
      )}) AND Subject LIKE '%Director Account Review%' AND Status = 'Completed'`,
    context
  );

  // Group by account and find the most recent DAR
  const lastDARByAccount = {};
  lastDARs.forEach((dar) => {
    const accountId = dar.WhatId;
    if (
      !lastDARByAccount[accountId] ||
      new Date(dar.CompletedDateTime) >
        new Date(lastDARByAccount[accountId].CompletedDateTime)
    ) {
      lastDARByAccount[accountId] = dar;
    }
  });

  // Calculate days since last DAR for each account
  const today = new Date();
  const overdueAccounts = [];
  let greenCount = 0,
    yellowCount = 0,
    redCount = 0;

  activeAccounts.forEach((account) => {
    const lastDAR = lastDARByAccount[account.Id];
    let daysSinceLastDAR = 0;

    if (lastDAR) {
      const lastDARDate = new Date(lastDAR.CompletedDateTime);
      daysSinceLastDAR = Math.floor(
        (today - lastDARDate) / (1000 * 60 * 60 * 24)
      );
    } else {
      // No DAR found, consider it overdue
      daysSinceLastDAR = 999; // Arbitrary large number
    }

    if (daysSinceLastDAR <= 90) {
      greenCount++;
    } else if (daysSinceLastDAR <= 120) {
      yellowCount++;
    } else {
      redCount++;
    }

    overdueAccounts.push({
      ...account,
      lastDARDate: lastDAR ? lastDAR.CompletedDateTime : null,
      daysSinceLastDAR: daysSinceLastDAR,
      lastDAROwner: lastDAR ? lastDAR.Owner?.Name : null,
      status:
        daysSinceLastDAR <= 90
          ? "Green"
          : daysSinceLastDAR <= 120
          ? "Yellow"
          : "Red",
    });
  });

  // Group by coordinator for breakdown
  const breakdown = {};
  overdueAccounts.forEach((account) => {
    const coordinatorName = account.Gargle_Coordinator__r?.Name || "Unknown";
    if (!breakdown[coordinatorName]) {
      breakdown[coordinatorName] = { Green: 0, Yellow: 0, Red: 0, total: 0 };
    }
    breakdown[coordinatorName][account.status]++;
    breakdown[coordinatorName].total++;
  });

  return {
    count: overdueAccounts.length,
    Green: greenCount,
    Yellow: yellowCount,
    Red: redCount,
    data: overdueAccounts,
    breakdown: breakdown,
  };
};

// Get overdue DARs for table view (formatted data)
export const getOverdueDARsForTable = async (
  context,
  selectedCoordinator = null
) => {
  const overdueDARs = await getOverdueDARs(context, selectedCoordinator);

  // Return the data array for table display
  return overdueDARs.data || [];
};
